import { useCallback, useContext } from 'react'

import { Text } from '@chakra-ui/react'
import { MODAL_TYPES } from '@editor/config/constants'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { FileProcessingStatus } from '@editor/interfaces/file'
import { Shape, ShapeDetectionResult, ShapeKeyType } from '@editor/interfaces/shape'
import { PendingAsyncJobToken } from '@editor/interfaces/system'
import { setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import { useInspectionItems } from '@editor/pages/projects/hooks/useInspectionItems'
import { useShapes } from '@editor/pages/projects/hooks/useShapes'
import { COMMON_MESSAGE } from '@editor/services/ErrorHandler'
import { checkJobStatusForDetectShapes } from '@editor/services/InspectionArea'
import { convertDetectionResultToShapes } from '@editor/services/Points'
import ShapeDetectionError from '@editor/services/errors/ShapeDetectionError'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setSelectedElementIds } from '@editor/store/tool'
import mixpanel from 'mixpanel-browser'
import { useSelector } from 'react-redux'

interface PollReturnValue {
  shapes: Shape[]
  reevaluate_volume_job_tokens?: PendingAsyncJobToken[]
}

export default function useDetectShapes(
  /**
   * Shape key to be detected.
   */
  shapeKey: ShapeKeyType,

  /**
   * Whether to throw an error if the polling fails
   * instead of showing a modal and returning.
   */
  throwError = true,
) {
  // Context
  const { getAccessToken } = useContext(UserContext)
  const { showModal, showErrorModal } = useContext(GlobalModalContext)
  const { fetchShapes } = useShapes()
  const { fetchInspectionItems } = useInspectionItems()

  // Store
  const dispatch = useAppDispatch()
  const project = useSelector((root: RootState) => root.page.project)
  const inspectionArea = useSelector((root: RootState) => root.page.inspectionArea)
  const inspectionSheet = useSelector((state: RootState) => state.inspectionItems.inspectionSheet)

  /**
   * Polls the server for completion of volume estimation.
   */
  const poll = useCallback(
    async (
      jobToken: string,
      interval = 5000,
      /**
       * Callback to be called during various stages of detecting shapes.
       */
      callbacks: {
        /**
         * Callback to be called after detecting shapes, but before updating the state.
         */
        onPostDetect?: (result: PollReturnValue) => Promise<boolean>
      } = {},
    ): Promise<PollReturnValue | null> => {
      if (
        !project ||
        inspectionArea?.downsample_status?.status !== FileProcessingStatus.SUCCEEDED ||
        !jobToken ||
        !inspectionSheet
      )
        return null

      const token = await getAccessToken()
      if (!token) return null

      return new Promise<PollReturnValue>((resolve, reject) => {
        const check = async () => {
          const errorResults: ShapeDetectionResult[] = []
          const jobResult = await checkJobStatusForDetectShapes(token, jobToken, showErrorModal)
          if (jobResult === null) {
            setTimeout(() => {
              void check()
            }, interval)
          } else if (jobResult === false) {
            reject(new Error(COMMON_MESSAGE))
          } else if (typeof jobResult === 'object') {
            const results = jobResult.list_shapes as ShapeDetectionResult[]
            const newShapes = convertDetectionResultToShapes(
              shapeKey,
              results.filter((shape) => !shape.error_id),
            ) as Shape[]
            errorResults.push(...(results.filter((shape) => !!shape.error_id) || []))

            const returnResult = {
              shapes: newShapes,
              reevaluate_volume_job_tokens: jobResult.reevaluate_volume_job_tokens,
            }

            if (newShapes.length > 0) {
              try {
                if (callbacks?.onPostDetect) await callbacks.onPostDetect(returnResult)

                // refetch inspection sheet items as various behavior related to estimate volume
                // depends on inspection item generated by estimateVolume API call
                // do this regardless of errors since if some succeeds, those need to be shown to user
                await Promise.all([fetchInspectionItems(), fetchShapes()])

                // set the new planes as selected planes
                dispatch(setSelectedElementIds(newShapes.map((shape) => shape.shape_id)))
              } catch (err) {
                reject(err instanceof Error ? err : new Error(String(err)))
                return
              }
            }

            const totalErrorShapes = errorResults.length

            // track with mixpanel
            mixpanel.track('Finish detecting shapes', {
              'Shape type': shapeKey,
              'Number of shapes (success)': jobResult.list_shapes.length - totalErrorShapes,
              'Number of shapes (error)': totalErrorShapes,
            })

            // if job has failed or no shape has been detected, show a message
            if (!jobResult.list_shapes.length) {
              dispatch(setAttentionText({ message: '' }))

              if (throwError) {
                reject(new Error('要素は検出されませんでした。'))
                return
              }

              showModal({
                body: '要素は検出されませんでした。',
                modalType: MODAL_TYPES.ALERT,
                title: '要素検出',
                confirmText: '了解',
              })
              // if there are some shapes failed to detected, show a message
            } else if (totalErrorShapes) {
              dispatch(setAttentionText({ message: '' }))

              if (throwError) {
                reject(
                  new ShapeDetectionError(`${totalErrorShapes} 箇所の領域で要素が検出されませんでした。`, errorResults),
                )
                return
              }

              showModal({
                body: (
                  <Text>
                    <Text fontWeight="bold" display="inline-block">
                      {totalErrorShapes}
                    </Text>
                    箇所の領域で要素が検出されませんでした。
                  </Text>
                ),
                modalType: MODAL_TYPES.ALERT,
                title: '要素検出',
                confirmText: '了解',
              })
            }

            resolve(returnResult)
          }
        }

        void check()
      })
    },
    [
      project,
      inspectionArea,
      inspectionSheet,
      shapeKey,
      throwError,
      dispatch,
      showErrorModal,
      getAccessToken,
      showModal,
      fetchShapes,
      fetchInspectionItems,
    ],
  )

  return {
    poll,
  }
}
